import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, Instagram, Facebook, MapPin } from "lucide-react";
import { CONTACT, waUrl, useContactNumber, useContactEmail, useBusinessHours } from "../../lib/contact";
import { pushGenerateLead } from "../../lib/gtmDataLayer";

const LOGO_URL = "/logo-footer.png?v=3";
const FACEBOOK_URL = "https://m.facebook.com/sridurgafoodsindia/";
const INSTAGRAM_URL = "https://www.instagram.com/sridurgafoods_india";

export default function Footer() {
  // iter53 — call number now reflects the live Admin → Settings value.
  const contact = useContactNumber();
  // iter71 — customer email is now live-editable from Admin → Settings.
  const email = useContactEmail();
  // iter72 — admin-editable business hours line.
  const businessHours = useBusinessHours();
  return (
    <footer className="bg-brand-dark text-brand-cream mt-10 sm:mt-12 lg:mt-14" data-testid="site-footer">
      <div className="container-x py-14 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="mb-6 flex md:justify-start justify-center">
              <img
                src={LOGO_URL}
                alt="Sri Durga Foods"
                className="h-24 sm:h-28 lg:h-32 w-auto select-none"
                draggable="false"
                data-testid="footer-logo"
              />
            </div>
            <p className="text-sm text-brand-cream/70 leading-relaxed max-w-md mb-6">
              The Taste Andhra Grew Up With. From mango pickles beside hot
              rice to podis, spices and culinary pastes used every day,
              Sri Durga Foods has been part of Andhra kitchens for more
              than 25 years. We continue to make food the way our customers
              expect — authentic, dependable and full of flavour.
            </p>
            <div className="flex items-center gap-3">
              <a href={waUrl()} target="_blank" rel="noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 hover:border-brand-turmeric hover:text-brand-turmeric transition-all" data-testid="footer-whatsapp" aria-label="WhatsApp">
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 hover:border-brand-turmeric hover:text-brand-turmeric transition-all" data-testid="footer-instagram" aria-label="Instagram">
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 hover:border-brand-turmeric hover:text-brand-turmeric transition-all" data-testid="footer-facebook" aria-label="Facebook">
                <Facebook className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="overline mb-5 !text-brand-turmeric">Shop</h4>
            <ul className="space-y-3 text-sm text-brand-cream/80">
              <li><Link to="/pickles" className="hover:text-brand-turmeric transition-colors">Pickles</Link></li>
              <li><Link to="/pastes" className="hover:text-brand-turmeric transition-colors">Culinary Pastes</Link></li>
              <li><Link to="/spices" className="hover:text-brand-turmeric transition-colors">Spices</Link></li>
              <li><Link to="/masala-powders" className="hover:text-brand-turmeric transition-colors">Masalas</Link></li>
              <li><Link to="/podis" className="hover:text-brand-turmeric transition-colors">Traditional Powders</Link></li>
              <li><Link to="/flours" className="hover:text-brand-turmeric transition-colors">Flours</Link></li>
              <li><Link to="/wood-pressed-oils" className="hover:text-brand-turmeric transition-colors">Wood Pressed Oils</Link></li>
              <li><Link to="/combos" className="hover:text-brand-turmeric transition-colors">Combos</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2" data-testid="footer-important-information">
            <h4 className="overline mb-5 !text-brand-turmeric">Important Information</h4>
            <ul className="space-y-3 text-sm text-brand-cream/80">
              <li><Link to="/terms-and-conditions" className="hover:text-brand-turmeric transition-colors" data-testid="footer-link-terms">Terms &amp; Conditions</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-brand-turmeric transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link></li>
              <li><Link to="/shipping-policy" className="hover:text-brand-turmeric transition-colors" data-testid="footer-link-shipping">Shipping Policy</Link></li>
              <li><Link to="/return-refund-policy" className="hover:text-brand-turmeric transition-colors" data-testid="footer-link-returns">Return &amp; Refund Policy</Link></li>
              <li><Link to="/contact" className="hover:text-brand-turmeric transition-colors" data-testid="footer-link-contact">Contact us</Link></li>
              <li><Link to="/fssai" className="hover:text-brand-turmeric transition-colors" data-testid="footer-link-fssai">FSSAI</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="overline mb-5 !text-brand-turmeric">Get in touch</h4>
            <ul className="space-y-3 text-sm text-brand-cream/80">
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-brand-turmeric flex-shrink-0" strokeWidth={1.5} /> {CONTACT.ADDRESS}</li>
              <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-brand-turmeric flex-shrink-0" strokeWidth={1.5} /> <a href={contact.tel} onClick={() => pushGenerateLead("phone", { source: "footer" })} className="hover:text-brand-turmeric" data-testid="footer-call-link">{contact.display}</a></li>
              <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-brand-turmeric flex-shrink-0" strokeWidth={1.5} /> <a href={email.mailto()} onClick={() => pushGenerateLead("email", { source: "footer" })} className="hover:text-brand-turmeric" data-testid="footer-email-link">{email.address}</a></li>
              <li className="flex items-center gap-3"><MessageCircle className="w-4 h-4 text-brand-turmeric flex-shrink-0" strokeWidth={1.5} /> <a href={waUrl()} onClick={() => pushGenerateLead("whatsapp", { source: "footer" })} target="_blank" rel="noreferrer" className="hover:text-brand-turmeric" data-testid="footer-whatsapp-link">WhatsApp · {CONTACT.WHATSAPP_DISPLAY}</a></li>
            </ul>
            {/* iter72 — subtle business-hours line beneath the contact list */}
            <p className="mt-3 text-xs text-brand-cream/60" data-testid="footer-business-hours">
              Business Hours: {businessHours}
            </p>
          </div>
        </div>

        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-brand-cream/50">
          <span>© {new Date().getFullYear()} Sri Durga Foods. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}
